import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, Check, X, GripVertical } from 'lucide-react';
import useDragReorder from '../../hooks/useDragReorder';
import { sans, mono, green, greenLight, amber, useTheme } from '../../tokens';
import {
  getMilestones, saveMilestones, newMilestoneId,
  subscribeToRoadmapChanges, MILESTONE_STATUSES,
} from '../../data/roadmapStore';
import {
  upsertMilestoneCompletionAlert, removeMilestoneCompletionAlert,
} from '../../data/alertsStore';

const STATUS_LABELS = {
  completed: 'Done',
  in_progress: 'Active',
  upcoming: 'Planned',
  future: '2027+',
};

function statusStyles(t, status) {
  if (status === 'in_progress') return { border: green, bg: greenLight, pillBg: green, pillColor: '#fff', textColor: green };
  if (status === 'completed') return { border: t.textMuted, bg: 'transparent', pillBg: t.textMuted, pillColor: '#fff', textColor: t.textSecondary };
  if (status === 'future') return { border: t.borderLight, bg: 'transparent', pillBg: t.borderLight, pillColor: t.textFaint, textColor: t.textFaint };
  return { border: amber, bg: 'transparent', pillBg: amber + '20', pillColor: amber, textColor: t.textSecondary };
}

export default function ProductRoadmap({ companyId, readOnly = false, selectedPeriod }) {
  const t = useTheme();
  // Any milestone that was previously saved with status=completed gets
  // hidden from the roadmap — completed items live only in Recent Updates.
  const [milestones, setMilestones] = useState(() => getMilestones(companyId).filter(m => m.status !== 'completed'));
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ label: '', period: '', status: 'upcoming' });

  // Re-hydrate when storage changes in another tab
  useEffect(() => subscribeToRoadmapChanges(() => setMilestones(getMilestones(companyId))), [companyId]);

  const startEdit = (m) => {
    setEditingId(m.id);
    setDraft({ label: m.label, period: m.period, status: m.status });
  };
  const cancelEdit = () => { setEditingId(null); setDraft({ label: '', period: '', status: 'upcoming' }); };
  // When a milestone is marked Done, it moves out of the Product Roadmap
  // and becomes an entry in Recent Updates. The admin can still delete the
  // update from Recent Updates if they change their mind.
  const markMilestoneDone = (milestone) => {
    upsertMilestoneCompletionAlert(companyId, milestone, selectedPeriod);
    const next = milestones.filter(m => m.id !== milestone.id);
    setMilestones(next);
    saveMilestones(companyId, next);
  };

  const saveEdit = () => {
    if (!draft.label.trim() || !draft.period.trim()) return;
    const prev = milestones.find(m => m.id === editingId);
    const updated = { ...prev, ...draft };
    if (draft.status === 'completed') {
      // Moved out of the roadmap entirely — push to Recent Updates.
      markMilestoneDone(updated);
      cancelEdit();
      return;
    }
    const next = milestones.map(m => m.id === editingId ? updated : m);
    setMilestones(next);
    saveMilestones(companyId, next);
    cancelEdit();
  };
  const remove = (id) => {
    const next = milestones.filter(m => m.id !== id);
    setMilestones(next);
    saveMilestones(companyId, next);
    // Also clean up any auto-generated completion alert for this milestone
    removeMilestoneCompletionAlert(companyId, id);
  };
  const drag = useDragReorder(milestones, (next) => {
    setMilestones(next);
    saveMilestones(companyId, next);
  });
  const addNew = () => {
    const m = { id: newMilestoneId(), label: 'New milestone', period: 'Q1 2026', status: 'upcoming' };
    const next = [...milestones, m];
    setMilestones(next);
    saveMilestones(companyId, next);
    startEdit(m);
  };

  const inputStyle = {
    padding: '4px 8px', borderRadius: 4, border: `1px solid ${t.border}`,
    background: t.bgCard, color: t.textPrimary, fontFamily: sans, fontSize: 12, outline: 'none',
  };

  return (
    <div>
      {milestones.map((m, i) => {
        const s = statusStyles(t, m.status);
        const isEditing = editingId === m.id;
        const rowDrag = !readOnly && !isEditing ? drag.rowProps(m.id) : {};
        const isDragTarget = drag.dragOverId === m.id;
        return (
          <div key={m.id}
            {...rowDrag}
            style={{
              ...(rowDrag.style || {}),
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderLeft: `3px solid ${isDragTarget ? green : s.border}`,
              background: isDragTarget ? greenLight : s.bg,
              borderRadius: '0 6px 6px 0', marginBottom: 4,
              transition: 'background 0.15s, border-color 0.15s',
            }}>
            {isEditing ? (
              <>
                <input
                  value={draft.period}
                  onChange={e => setDraft({ ...draft, period: e.target.value })}
                  placeholder="Q1 2026"
                  style={{ ...inputStyle, width: 90, fontFamily: mono, fontSize: 10 }}
                />
                <input
                  value={draft.label}
                  onChange={e => setDraft({ ...draft, label: e.target.value })}
                  placeholder="Milestone label"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <select
                  value={draft.status}
                  onChange={e => setDraft({ ...draft, status: e.target.value })}
                  style={{ ...inputStyle, width: 110, fontSize: 10 }}
                >
                  {MILESTONE_STATUSES.map(st => (
                    <option key={st} value={st}>{STATUS_LABELS[st] || st}</option>
                  ))}
                </select>
                <button onClick={saveEdit} title="Save" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: green, padding: 4 }}>
                  <Check size={16} />
                </button>
                <button onClick={cancelEdit} title="Cancel" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}>
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <div style={{ minWidth: 80 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: mono, color: s.textColor }}>{m.period}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, fontFamily: sans, color: m.status === 'future' ? t.textFaint : t.textPrimary }}>{m.label}</span>
                </div>
                <span style={{
                  fontSize: 8, fontWeight: 700, fontFamily: sans, textTransform: 'uppercase',
                  padding: '2px 8px', borderRadius: 10,
                  background: s.pillBg, color: s.pillColor,
                }}>{STATUS_LABELS[m.status] || m.status}</span>
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
                    <span title="Drag to reorder" style={{ display: 'flex', alignItems: 'center', color: t.textFaint, padding: 2, cursor: 'grab' }}>
                      <GripVertical size={12} />
                    </span>
                    <button onClick={() => markMilestoneDone(m)} title="Mark as Done (moves to Recent Updates)"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: green, padding: 4 }}>
                      <Check size={14} />
                    </button>
                    <button onClick={() => startEdit(m)} title="Edit"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}>
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => remove(m.id)} title="Delete"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#C0392B', padding: 4 }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <button onClick={addNew} style={{
          marginTop: 6, padding: '6px 12px', borderRadius: 6,
          border: `1px dashed ${t.border}`, background: 'transparent',
          color: t.textMuted, fontFamily: sans, fontSize: 11, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Plus size={12} /> Add milestone
        </button>
      )}

      {milestones.length === 0 && readOnly && (
        <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 11, color: t.textFaint, fontFamily: sans }}>
          No milestones published yet.
        </div>
      )}
    </div>
  );
}
